import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";
import { TOUR_STEPS } from "./tour-steps";

const TOTAL_STOPS = TOUR_STEPS.length;

interface TourState {
  isActive: boolean;
  currentStop: number;             // 0..TOTAL_STOPS-1 when active; -1 when inactive
  completedStops: number[];
}

interface TourApi extends TourState {
  total: number;
  startTour: () => void;
  nextStop: () => void;
  prevStop: () => void;
  exitTour: () => void;
  goToStop: (n: number) => void;
}

const TourContext = createContext<TourApi | null>(null);

// sessionStorage keys for stop persistence — cleared when the tab closes.
const SS_STOP_KEY = "aip:tour:currentStop";
const SS_ACTIVE_KEY = "aip:tour:isActive";

function loadCompleted(): number[] {
  try {
    const raw = typeof window !== "undefined" ? window.sessionStorage.getItem("aip:tour:completed") : null;
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((n: unknown): n is number => typeof n === "number");
  } catch {
    return [];
  }
}

function loadSavedStop(): { isActive: boolean; currentStop: number } {
  try {
    const active = window.sessionStorage.getItem(SS_ACTIVE_KEY);
    const stop = window.sessionStorage.getItem(SS_STOP_KEY);
    if (active === "true" && stop !== null) {
      const n = parseInt(stop, 10);
      if (!isNaN(n) && n >= 0) return { isActive: true, currentStop: n };
    }
  } catch { /* ignore */ }
  return { isActive: false, currentStop: -1 };
}

function saveCompleted(stops: number[]): void {
  try {
    window.sessionStorage.setItem("aip:tour:completed", JSON.stringify(stops));
  } catch {
    // ignore quota / disabled storage
  }
}

function saveStopState(isActive: boolean, currentStop: number): void {
  try {
    window.sessionStorage.setItem(SS_ACTIVE_KEY, String(isActive));
    window.sessionStorage.setItem(SS_STOP_KEY, String(currentStop));
  } catch { /* ignore */ }
}

function hashRouteFor(step: { route: string }): string {
  // Step routes in tour-steps.ts are written like "/#/dashboards/ae" so
  // the registry reads naturally. Strip the leading "/#" to assign to
  // window.location.hash (which already implies the "#" prefix).
  return step.route.startsWith("/#") ? step.route.slice(2) : step.route;
}

export function TourProvider({ children }: { children: ReactNode }) {
  const savedStop = loadSavedStop();
  const [isActive, setIsActive]         = useState(savedStop.isActive);
  const [currentStop, setCurrentStop]   = useState(savedStop.currentStop);
  const [completedStops, setCompleted]  = useState<number[]>(() => loadCompleted());
  const navTimer = useRef<number | null>(null);

  useEffect(() => { saveCompleted(completedStops); }, [completedStops]);
  useEffect(() => { saveStopState(isActive, currentStop); }, [isActive, currentStop]);

  // ── Route navigation ────────────────────────────────────────────────────
  const navigateTo = useCallback((stopIdx: number) => {
    const step = TOUR_STEPS[stopIdx];
    if (!step) return;
    const target = hashRouteFor(step);
    const current = window.location.hash.replace(/^#/, "");
    if (current !== target) {
      window.location.hash = target;
    }
  }, []);

  // ── Lifecycle ───────────────────────────────────────────────────────────
  const startTour = useCallback(() => {
    setIsActive(true);
    setCurrentStop(0);
    navigateTo(0);
  }, [navigateTo]);

  const exitTour = useCallback(() => {
    setIsActive(false);
    setCurrentStop(-1);
    if (navTimer.current) {
      window.clearTimeout(navTimer.current);
      navTimer.current = null;
    }
    try {
      window.sessionStorage.removeItem(SS_ACTIVE_KEY);
      window.sessionStorage.removeItem(SS_STOP_KEY);
    } catch { /* ignore */ }
  }, []);

  const goToStop = useCallback((n: number) => {
    if (n < 0 || n >= TOTAL_STOPS) return;
    setIsActive(true);
    setCurrentStop(n);
    navigateTo(n);
  }, [navigateTo]);

  const nextStop = useCallback(() => {
    setCurrentStop((prev) => {
      if (prev < 0) return prev;
      // Mark the leaving stop as completed.
      setCompleted((c) => (c.includes(prev) ? c : [...c, prev]));
      const next = prev + 1;
      if (next >= TOTAL_STOPS) {
        // Finished the whole tour.
        setIsActive(false);
        return -1;
      }
      navigateTo(next);
      return next;
    });
  }, [navigateTo]);

  const prevStop = useCallback(() => {
    setCurrentStop((prev) => {
      if (prev <= 0) return prev;
      const next = prev - 1;
      navigateTo(next);
      return next;
    });
  }, [navigateTo]);

  const api = useMemo<TourApi>(() => ({
    isActive,
    currentStop,
    completedStops,
    total: TOTAL_STOPS,
    startTour,
    nextStop,
    prevStop,
    exitTour,
    goToStop,
  }), [isActive, currentStop, completedStops, startTour, nextStop, prevStop, exitTour, goToStop]);

  return <TourContext.Provider value={api}>{children}</TourContext.Provider>;
}

export function useTour(): TourApi {
  const ctx = useContext(TourContext);
  if (!ctx) {
    throw new Error("useTour must be used inside <TourProvider>");
  }
  return ctx;
}
